import type { getEquityDetailContentResponse } from '#/api/generated/content/content';
import { useGetEquityDetailContent } from '#/api/generated/content/content';
import type { ContentPage } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type {
  ContentArticleCard,
  DefinitionTerm,
  StaticFallback,
} from '#/content/lib';
import {
  contentArticleCards,
  contentText,
  isContentPage,
  normalizeDefinitions,
} from '#/content/lib';
import { CONTENT_CACHE_QUERY_POLICY } from '#/content/queries';
import { shouldUseFallbackContent } from '#/lib/api-client';

/**
 * The equity-detail content document: view model, fallback copy, merge, and
 * hook. Section intros are Drupal rich text destined for `RichText`, so they
 * carry their HTML through unstripped. Glossary terms (tooltips + stat
 * descriptions) are returned separately as a slug lookup — see
 * {@link normalizeDefinitions}.
 */
export interface EquityDetailContentView {
  riskIntro: string;
  filingsIntro: string;
  topHoldersIntro: string;
  priceIntro: string | null;
  learnMoreArticles: ContentArticleCard[] | null;
}

/**
 * Tier 1 fallback equity-detail copy, never null
 */
export const EQUITY_DETAIL_FALLBACK: StaticFallback<EquityDetailContentView> = {
  riskIntro:
    'These risk metrics are provided to contribute to a fuller picture of the risk of the security. Note that historical performance does not necessarily indicate future performance.',
  filingsIntro:
    "There's a wealth of financial data about a company's operations, investments, indebtedness and much more easily accessible to investors who know where to look. These documents are provided by the SEC.",
  topHoldersIntro:
    'These mutual funds and ETFs are the top holders of this security. They are provided as informational, and may be part of an investment strategy for portfolio diversification.',
};

/**
 * Merge fields and fallbacks, null is the total-failure state
 */
export function mergeEquityDetail(
  page: ContentPage | null | undefined,
): EquityDetailContentView {
  return {
    riskIntro:
      contentText(page?.risk_section_intro) ?? EQUITY_DETAIL_FALLBACK.riskIntro,
    filingsIntro:
      contentText(page?.filings_section_intro) ??
      EQUITY_DETAIL_FALLBACK.filingsIntro,
    topHoldersIntro:
      contentText(page?.top_fund_holders_section_intro) ??
      EQUITY_DETAIL_FALLBACK.topHoldersIntro,
    priceIntro: contentText(page?.price_cost_section_intro),
    learnMoreArticles: contentArticleCards(page, 'learn_more_articles'),
  };
}

interface UseEquityDetailContentResult {
  content: EquityDetailContentView;
  /** Glossary terms for the page, keyed by slug (empty on failure). */
  definitions: Map<string, DefinitionTerm>;
  /** True when the content API failed and fallback copy is being shown. */
  isDegraded: boolean;
}

interface EquityDetailContentData {
  content: EquityDetailContentView;
  definitions: Map<string, DefinitionTerm>;
}

/**
 * Stable fallback returned while the query is unsettled or failed. Reused by
 * reference so the detail view's seed effect doesn't loop on a fresh Map.
 */
const FALLBACK_CONTENT_DATA: EquityDetailContentData = {
  content: mergeEquityDetail(null),
  definitions: normalizeDefinitions(null),
};

function selectEquityDetailContent(
  response: getEquityDetailContentResponse,
): EquityDetailContentData {
  const page = isContentPage(response.data) ? response.data : null;
  return {
    content: mergeEquityDetail(page),
    definitions: normalizeDefinitions(page),
  };
}

/**
 * Equity-detail content, merged against fallback copy.
 *
 * Warmed via `equityDetailContentOptions()` on `/$category/$id` with
 * `staleTime: Infinity` for no re-renders for the life of the session.
 */
export function useEquityDetailContent(): UseEquityDetailContentResult {
  const query = useGetEquityDetailContent({
    query: {
      ...CONTENT_CACHE_QUERY_POLICY,
      select: selectEquityDetailContent,
    },
  });

  const data = query.isSuccess ? query.data : FALLBACK_CONTENT_DATA;

  return {
    content: data.content,
    definitions: data.definitions,
    isDegraded: shouldUseFallbackContent(query.error),
  };
}
